import { type Locale } from '@sjuni/types';
import { Input } from '@sjuni/ui';
import { AlertTriangle, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { setLocale, type TranslatedValue, valueFor } from '@/lib/forms/translatable';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.3.
 *
 * "Auto-generated from the title with Georgian→Latin transliteration, shown
 * read-only with an 'Edit' affordance. Editing a published record's slug shows:
 * *'This will change the page's address. A redirect from the old address will
 * be created automatically.'* and writes a `redirects` row on save."
 *
 * The transliteration itself is `App\Support\Slugger` on the server, not a
 * second implementation here (CLAUDE.md rule 4). This field's job is to leave
 * the slug alone unless asked: an empty value means "generate it", and the
 * moment the browser guesses one, the guess is what gets saved and the two
 * implementations start to disagree.
 *
 * Read-only by default because a slug is an address. §5.1 of the brief makes
 * them immutable once published for the same reason §5 of the migration doc
 * gives — inbound links from ministry sites and academic citations are very
 * hard to get changed.
 */
export interface SlugFieldProps {
  value: TranslatedValue | null | undefined;
  onChange: (next: TranslatedValue) => void;
  /** The slug the record was loaded with, per locale — absent for a new one. */
  original?: TranslatedValue | null;
  /** Whether the record is live. Only then is a change a broken link. */
  published: boolean;
  error?: string;
  disabled?: boolean;
}

export function SlugField({
  value,
  onChange,
  original,
  published,
  error,
  disabled,
}: SlugFieldProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const [editing, setEditing] = useState(false);

  const current = valueFor(value, locale);
  const previous = original?.[locale] ?? null;

  // A change that matters: the record is live and the address is moving.
  const willRedirect = published && previous !== null && current !== '' && current !== previous;

  return (
    <div>
      <Input
        label={t('form.slug.label')}
        name="slug"
        value={current}
        readOnly={!editing}
        disabled={disabled}
        error={error}
        hint={editing ? t('form.slug.editingHint') : hintFor(t, current, locale)}
        onChange={(event) => onChange(setLocale(value, locale, event.target.value))}
      />

      {editing ? null : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={disabled}
          className="mt-1.5 inline-flex items-center gap-1 text-caption font-semibold text-link underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          <Pencil size={12} strokeWidth={1.75} aria-hidden="true" />
          {t('form.slug.edit')}
        </button>
      )}

      {willRedirect ? (
        /*
         * `role="status"` rather than `alert`: this is a consequence being
         * explained, not a mistake being reported. It appears as they type,
         * and an assertive live region would interrupt them mid-word.
         */
        <p
          role="status"
          className="mt-2 flex items-start gap-2 rounded-card border-s-4 border-gold-500 bg-surface-alt p-3 text-caption text-secondary"
        >
          <AlertTriangle
            size={14}
            strokeWidth={1.75}
            aria-hidden="true"
            className="mt-0.5 shrink-0"
          />
          {t('form.slug.redirectWarning', { from: previous })}
        </p>
      ) : null}
    </div>
  );
}

function hintFor(
  t: (key: string, options?: Record<string, unknown>) => string,
  current: string,
  locale: Locale,
): string {
  return current === ''
    ? t('form.slug.generatedHint')
    : t('form.slug.addressHint', { locale: locale.toUpperCase() });
}
